import { z } from 'zod';
import { ReferrerHealixSchema } from './ReferrerHealixSchema';
import { ReferralHealixPatientSchema } from './ReferralHealixPatientSchema';
import { IObject } from '../../IObject';
import { ReferralBaseSchema } from '../ReferralBaseSchema';
import { ReferralCoverageSchema } from '../ReferralCoverageSchema';
import {
  ReferralIdSchema,
  ReferralStoredDocumentsSchema,
} from '../ReferralDocumentsSchema';
import { ReferralHealixDiagnosisSchema } from './ReferralHealixDiagnosisSchema';
import {
  ReferralConsentRequestSchema,
  ReferralConsentSchema,
  requiresPatientPhoneForConsent,
  requiresPatientConsentEvidence,
} from '../ReferralConsentSchema';
import { ReferralNotesSchema } from '../ReferralNotesSchema';
import { allJoinReasonIds } from '../../../data/joinReasons';
import { joinReasonIdSchema } from '../ReferralJoinReasonSchema';
import { withLegacyPlanIdentifiers } from '../hoistPlanIdentifiersToPatient';
import { readHealixFallbackReferral } from './readHealixFallbackReferral';

// Healix referral that is sent from Healix system via REST API
const healixBaseFields = {
  // Optional, unlike the organizations whose web form mints it: Healix posts
  // straight to the API, so it either asks for an ID first (POST /referrals/id,
  // which lets it upload the documents before the referral) or lets the
  // endpoint generate one
  id: ReferralIdSchema.optional(),
  // carries the plan identifiers: a body's top-level `claimNumber` is
  // hoisted into it while parsing (see the create schemas below)
  patient: ReferralHealixPatientSchema,
  coverage: ReferralCoverageSchema,
  diagnosis: ReferralHealixDiagnosisSchema,
  referrer: ReferrerHealixSchema,
  // The free-text description of the referral, for the CNS. The referral form
  // asks the referrer for it; an integration may have none to send — the
  // Healix export carries no description at all (PPL-3889) — so it is
  // optional, and `null` is read as absent, the way that export writes an
  // empty field. Sent as text, it must say something.
  details: z.preprocess(
    (value) => (value === null ? undefined : value),
    z
      .string('Description should be text')
      .min(1, 'Description is required')
      .max(4000, 'Description cannot be longer than 4000 characters')
      .optional(),
  ),
  joinReasonId: joinReasonIdSchema(allJoinReasonIds),
  // Documents Healix attaches to the referral, sent as the canonical
  // `documents` array of the body (PPL-3549) and stored in the structure
  // every organization shares
  documents: ReferralStoredDocumentsSchema,
  // Where the referral came from ('web-healix', 'healix-api'). Stated by the
  // endpoint it arrived at and never read from the body, which anyone can
  // post — see postHealixReferral
  source: z.string().optional(),
  // Optional free text the referrer typed on the referral form. Internal
  // only: it is never shown to the member (see ReferralNotesSchema)
  notes: ReferralNotesSchema,
  // How we know the patient consented to being referred. The REQUEST form:
  // a body states the EVIDENCE only — the status and what we record when we
  // ask are ours to write (see ReferralConsentSchema; the stored schema
  // below carries the full shape)
  consent: ReferralConsentRequestSchema,
};

const HealixBaseSchemaObject = z
  .object(healixBaseFields)
  .check(requiresPatientPhoneForConsent);

// The plain object shape, for the OpenAPI page to spread (`.shape`); the
// schemas below wrap it to keep the fallback paths working
export const HealixReferralInterSystemObjectSchema = HealixBaseSchemaObject;

// The fallback paths a Healix body may still use, read into the one shape
// above: the names of the Healix spec (readHealixFallbackReferral) and the
// top-level plan identifiers every organization's form still sends
// (hoistPlanIdentifiersToPatient). Both readers leave a body already written
// our way untouched.
const withHealixFallback = <T extends z.ZodType>(schema: T) =>
  z.preprocess(readHealixFallbackReferral, withLegacyPlanIdentifiers(schema));

// The body an integration may send: everything the form sends, with the
// consent left out — referring through the API IS the attestation, which
// postHealixReferralInterSystem fills in before the create schema below
// validates the body (referrerAttestedConsent). Both ways in are parsed by
// that one schema; this is what the API's shape is documented and tested as.
export const HealixReferralInterSystemSchema = withHealixFallback(
  HealixBaseSchemaObject,
);

// A Healix referral as it is created, whichever way it came in: the web form
// ASKS the referrer how the patient consented, so the answer is required —
// and an integration's call has already been turned into the same answer.
export const ReferralCreateHealixSchema = withHealixFallback(
  HealixBaseSchemaObject.check(requiresPatientConsentEvidence),
);

// Healix Referral how it stored in the database
export const ReferralHealixSchema = ReferralBaseSchema.extend({
  // override default Referral fields with the Healix types
  ...healixBaseFields,
  // as stored: the evidence and status the body stated, plus whatever we
  // recorded when we asked the patient ourselves
  consent: ReferralConsentSchema,
});

export type HealixReferralInterSystem = z.infer<
  typeof HealixReferralInterSystemSchema
>;

export type ReferralHealixData = z.infer<typeof ReferralHealixSchema>;
export type ReferralHealix = IObject.HasIdTimestamped<ReferralHealixData>;
export type ReferralCreateHealix = z.infer<typeof ReferralCreateHealixSchema>;
